import { useState } from 'react';
import { Input } from 'src/components/ui/input';
import { useIsChatStreaming } from 'src/content/ChatV2/hooks/useIsChatStreaming';
import ActiveStreamDots from 'src/layouts/SidebarV2/components/ActiveStreamDots';
import { DeleteChatDialog } from 'src/layouts/SidebarV2/dialogs';
import { sidebarIcons } from 'src/layouts/SidebarV2/icons';
import { IconAction, NavItem } from 'src/layouts/SidebarV2/primitives';
import type { ChatHistoryItem } from 'src/layouts/SidebarV2/types';
import { resolveChatHistoryLink } from 'src/layouts/SidebarV2/utils';

export interface ChatRowProps {
  chat: ChatHistoryItem;
  onRename: (id: number, title: string) => void;
  onDelete: (id: number) => void;
}

const ChatRow = ({ chat, onRename, onDelete }: ChatRowProps) => {
  const isStreaming = useIsChatStreaming(chat.id);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(chat.title);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const commitRename = () => {
    const next = draft.trim();
    if (next && next !== chat.title) {
      onRename(chat.id, next);
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Input
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitRename}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commitRename();
          } else if (e.key === 'Escape') {
            setDraft(chat.title);
            setIsEditing(false);
          }
        }}
        className="tw:-outline-offset-2"
      />
    );
  }

  const actionCell = (
    <>
      <IconAction
        icon={sidebarIcons.edit}
        label="Rename chat"
        onClick={() => {
          setDraft(chat.title);
          setIsEditing(true);
        }}
      />
      <IconAction
        icon={sidebarIcons.trash}
        label="Delete chat"
        onClick={() => setConfirmOpen(true)}
      />
    </>
  );

  return (
    <>
      <NavItem
        name={chat.title}
        trailing={isStreaming ? <ActiveStreamDots /> : undefined}
        to={resolveChatHistoryLink(chat)}
        actionCell={actionCell}
      />

      <DeleteChatDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={() => onDelete(chat.id)}
      />
    </>
  );
};

export { ChatRow };
